import { Injectable, OnDestroy, inject } from '@angular/core';

import { MatDialog } from '@angular/material/dialog';

import { FsApi, RequestConfig } from '@firestitch/api';
import { nameValue } from '@firestitch/common';
import { ItemType } from '@firestitch/filter';
import { FsGalleryItem } from '@firestitch/gallery';
import { DisplayUploadStatus } from '@firestitch/upload';
import { FsWebSocket } from '@firestitch/web-socket';

import { Observable, of, Subject } from 'rxjs';
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { HttpContext } from '@angular/common/http';

import {
    Conversation,
    ConversationConfig,
    ConversationItem,
    ConversationItemFile,
    ConversationItemMessage,
    ConversationParticipant,
} from '@libs/conversations/types';


import { AclService, ExperienceService } from '@app/core/services';
import { EnvironmentData, OrganizationData } from '@common/data';
import { AclPermission, ConversationState, DialogWidth } from '@common/enums';

import { ConversationStartComponent } from '../components';


@Injectable({
  providedIn: 'root',
})
export class ConversationsService implements OnDestroy {
  private _api = inject(FsApi);
  private _dialog = inject(MatDialog);
  private _fsWebSocket = inject(FsWebSocket);
  private _experienceService = inject(ExperienceService);
  private _environmentData = inject(EnvironmentData);
  private _organizationData = inject(OrganizationData);
  private _aclService = inject(AclService);


  private _unreadStats$ = new Subject();
  private _conversationsFiltersChange$ = new Subject<void>();
  private _url = '';
  private _destroy$ = new Subject();

  public get unreadStats$(): Observable<any> {
    return this._unreadStats$;
  }

  /**
   * TU-T9154: emits when a conversations filter changes so the pane can
   * refresh the tab counts. The list reloads itself on filter change; only
   * the counts need the nudge.
   */
  public get conversationsFiltersChange$(): Observable<void> {
    return this._conversationsFiltersChange$;
  }

  public save(url, data) {
    return data.id ?
      this._api.put(`${this._url}${url}/${data.id}`, data) :
      this._api.post(`${this._url}${url}`, data);
  }

  public delete(url, data: any = {}) {
    return this._api.delete(`${this._url}${url}/${data.id}`, {});
  }

  public get(url, data: any = {}, config?: RequestConfig) {
    return this._api.get(`${this._url}${url}`, data, config);
  }

  public post(url, data, config?: RequestConfig) {
    return this._api.post(`${this._url}${url}`, data, config);
  }

  public get conversationConfig(): ConversationConfig {
    return {
      conversationsGet: (query?: any, config?: RequestConfig) => {
        query = {
          ...query,
          programs: true,
          environments: true,
          organizations: true,
          environmentAvatars: true,
        };

        if (query.conversationId) {
          query.state = [ConversationState.Open, ConversationState.Closed]
            .join(',');
        }

        return this._api.get(`${this._url}conversations`, query, config);
      },
      conversationsStats: (query?: any) => {
        return this._api.get(`${this._url}conversations/stats`, query);
      },
      conversationSave: (conversation: Conversation) => {
        return of(true)
          .pipe(
            switchMap(() => {
              return conversation.id ?
                of(conversation)
                : of(true)
                  .pipe(
                    switchMap(() => {
                      return this._environmentData.count()
                        .pipe(
                          switchMap((count) => {
                            if (count > 1) {
                              return this._dialog.open(ConversationStartComponent, { width: DialogWidth.ExtraSmall })
                                .afterClosed();
                            }

                            return this._environmentData.gets({ limit: 1 })
                              .pipe(
                                map((environments) => environments[0]),
                              );
                          }),
                        );
                    }),
                    filter((environment) => !!environment?.id),
                    map((environment) => {
                      return {
                        ...conversation,
                        environmentId: environment.id,
                      };
                    }),
                    takeUntil(this._destroy$),
                  );
            }),
            switchMap((response) => this.save('conversations', response)),
            map((response) => response.conversation),
          );
      },
      conversationDelete: (conversation: Conversation) => {
        return this.delete('conversations', conversation);
      },
      conversationRead: (
        conversation: Conversation,
        conversationItem: ConversationItem,
      ) => {
        return this.post(
          `conversations/${conversation.id}/read`,
          { conversationItemId: conversationItem.id },
        )
          .pipe(
            tap(() => {
              this.triggerStatsUpdate();
            }),
          );
      },
      conversationItemsGet: (conversation: Conversation, query?: any) => {
        return this.get(`conversations/${conversation.id}/items`, query);
      },
      conversationItemSave: (
        conversationItem: ConversationItem | ConversationItemMessage,
      ) => {
        return this.save(`conversations/${conversationItem.conversationId}/items`, conversationItem)
          .pipe(
            map((response) => response.conversationItem),
          );
      },
      conversationItemDelete: (
        conversationItem: ConversationItem | ConversationItemMessage,
      ) => {
        return this
          .delete(`conversations/${conversationItem.conversationId}/items`, conversationItem)
          .pipe(
            map((response) => response.conversationItem),
          );
      },
      conversationItemFilePost: (
        conversationItem: ConversationItem,
        file: Blob,
      ) => {
        return this
          .post(
            `conversations/${conversationItem.conversationId}/items/${conversationItem.id}/files`,
            { file },
            {
              context: new HttpContext().set(DisplayUploadStatus, false),
            },
          );
      },
      conversationItemFileDownload: (
        conversationItem: ConversationItem,
        conversationItemFileId: number,
      ) => {
        this._api
          .createApiFile(
            `conversations/${conversationItem.conversationId}/items/${conversationItem.id}/files/` +
            `${conversationItemFileId}/download`,
          )
          .download();
      },
      conversationParticipantsGet: (
        conversation: Conversation,
        query?: any,
      ) => {
        query.order = 'accounts.first_name,asc';

        return this.get(`conversations/${conversation.id}/participants`, query);
      },
      conversationParticipantSave: (
        conversation: Conversation,
        conversationParticipant: ConversationParticipant,
      ) => {
        return this.save(`conversations/${conversation.id}/participants`, conversationParticipant)
          .pipe(
            map((response) => response.conversationParticipant),
          );
      },
      conversationParticipantAdd: (conversation: Conversation, data: any) => {
        return this.save(`conversations/${conversation.id}/participants`, data);
      },
      conversationParticipantDelete: (
        conversation: Conversation,
        conversationParticipant: ConversationParticipant,
      ) => {
        return this.delete(`conversations/${conversation.id}/participants`, conversationParticipant);
      },
      conversationParticipantSession: (conversation: Conversation) => {
        return this.get(`conversations/${conversation.id}/participants/session`)
          .pipe(
            map((response) => response.conversationParticipant),
          );
      },
      conversationParticipantBulk: (conversation: Conversation, data: any) => {
        return this.post(`conversations/${conversation.id}/participants/bulk`, data);
      },
      accountsGet: (conversation: Conversation, query: any) => {
        return this.get(`accounts/conversations/${conversation.id}/available`, query);
      },
      websocketService: () => {
        return this._fsWebSocket;
      },
      mapAccount: (account: any) => {
        return {
          id: account.id,
          firstName: account.firstName,
          lastName: account.lastName,
          avatar: {
            tiny: account.avatar.tiny,
          },
        };
      },
      mapGalleryItem: (
        conversationItem: ConversationItem,
        conversationItemFile: ConversationItemFile,
      ): FsGalleryItem => {
        const url = this._api
          .createApiFile(
            `conversations/${conversationItem.conversationId}/items/${conversationItem.id}/files/` +
            `${conversationItemFile.id}/download`,
            conversationItemFile.file.filename,
          );

        const preview = this._api
          .createApiFile(
            `conversations/${conversationItem.conversationId}/items/${conversationItem.id}/files/` +
            `${conversationItemFile.id}/preview`,
            conversationItemFile.file.filename,
          );

        return {
          guid: String(conversationItemFile.id),
          name: conversationItemFile.file.filename,
          preview,
          url,
          index: conversationItemFile.id,
          data: conversationItemFile,
          extension: conversationItemFile.file.extension,
        };
      },
      conversationsFilters: [
        {
          name: 'organizationId',
          type: ItemType.AutoComplete,
          label: 'Organization',
          // TU-T9154: staff/admin only — hidden in the parent inbox.
          hide: this._experienceService.isExperienceParent,
          values: (keyword) => {
            return this._organizationData
              .gets({
                keyword,
                // Rank by relevance while typing, name on the empty list —
                // same treatment as the shared org filter (TU-T8973).
                order: keyword ? 'relevance' : 'name,asc',
                // Only organizations whose conversations the account can read.
                // OrganizationsView bypasses this for app admins, matching the
                // conversations ACL.
                hasPermission: AclPermission.Conversation,
              })
              .pipe(
                map((response) => nameValue(response, 'name', 'id')),
              );
          },
          change: () => this._conversationsFiltersChange$.next(),
        },
      ],
      leaveConversation: {
        show: () => this._experienceService.isExperienceParent || !this._aclService.hasWrite(AclPermission.Conversation)
          ? false
          : true,
      },
      startConversation: {
        disabled: () => this._hasAvailableConversations().pipe(map((value) => !value)),
        tooltip: () => {
          return this._hasAvailableConversations().pipe(map((hasAvailableConversations) => {
            return !hasAvailableConversations ? 'None of your current organizations use this feature.' : '';
          }));
        },
        show: () => {
          return this._hasAvailableConversations()
            .pipe(
              map((hasAvailableConversations) => {
                if (
                  this._experienceService.isExperienceOrganization &&
                  this._aclService.hasWrite(AclPermission.Conversation)
                ) {
                  return true;
                }

                return !(this._experienceService.isExperienceParent && !hasAvailableConversations);
              }),
            );
        },
      },
    };
  }

  public triggerStatsUpdate(): void {
    this.conversationConfig.conversationsStats({ account: true })
      .pipe(
        takeUntil(this._destroy$),
      )
      .subscribe((data: any) => {
        this._unreadStats$.next(data);
      });
  }

  public ngOnDestroy(): void {
    this._destroy$.next(null);
    this._destroy$.complete();
  }

  private _hasAvailableConversations(): Observable<boolean> {
    if (this._aclService.hasPermissionAdmin()) {
      return of(true);
    }

    return this._environmentData.availableForConversationCount().pipe(
      map((count) => {
        return !!count;
      }),
    );
  }
}
